import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '../../lib/utils';
import {
  LayoutDashboard,
  Users,
  ShoppingCart,
  Settings,
  ChevronDown,
  FileText,
  PieChart,
  Building,
  X,
  Megaphone,
  DollarSign
} from 'lucide-react';
import { Button } from '../ui/button';

interface NavItemProps {
  icon: React.ReactNode;
  title: string;
  path?: string;
  isActive?: boolean;
  children?: { title: string; path: string }[];
  onItemClick?: () => void;
}

interface SidebarProps {
  isOpen: boolean;
  closeSidebar: () => void;
}

const NavItem: React.FC<NavItemProps> = ({
  icon,
  title,
  path,
  isActive = false,
  children,
  onItemClick,
}) => {
  const location = useLocation();

  // Helper function to check if a path matches (including query params)
  const pathMatches = (childPath: string) => {
    if (!childPath) return false;
    const [pathname, search] = childPath.split('?');
    const currentPathname = location.pathname;
    const currentSearch = location.search;

    // Check if pathname matches
    if (pathname !== currentPathname) return false;

    // If there's a query parameter in the child path, check if it matches
    if (search) {
      const params = new URLSearchParams(search);
      const currentParams = new URLSearchParams(currentSearch);
      // Check if all params in child path exist in current location
      const paramsArray = Array.from(params.entries());
      for (let i = 0; i < paramsArray.length; i++) {
        const [key, value] = paramsArray[i];
        if (currentParams.get(key) !== value) return false;
      }
      return true;
    }

    return true;
  };

  // Check if current path matches this nav item or its children
  const isCurrentPath = path && pathMatches(path);
  const hasActiveChild = children?.some(child => pathMatches(child.path));
  const computedIsActive = isActive || isCurrentPath || hasActiveChild;

  // Auto-expand if there's an active child
  const [isOpen, setIsOpen] = useState(hasActiveChild);

  // Sync open state with active child state
  useEffect(() => {
    setIsOpen(!!hasActiveChild);
  }, [location.pathname, hasActiveChild]);

  if (children) {
    return (
      <div className="flex flex-col">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={cn(
            "flex items-center justify-between w-full px-3 py-2.5 text-sm font-medium rounded-md transition-colors",
            computedIsActive || isOpen
              ? "text-primary-foreground bg-primary"
              : "text-muted-foreground hover:text-foreground hover:bg-muted"
          )}
        >
          <div className="flex items-center gap-3">
            {icon}
            <span className="truncate">{title}</span>
          </div>
          <ChevronDown
            className={cn("h-4 w-4 transition-all", isOpen && "rotate-180")}
          />
        </button>
        {isOpen && (
          <div className="ml-6 mt-1 space-y-1">
            {children.map((child) => {
              const isChildActive = pathMatches(child.path);
              return (
                <Link
                  key={child.path}
                  to={child.path}
                  onClick={onItemClick}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2 text-sm rounded-md transition-colors",
                    isChildActive
                      ? "text-primary bg-primary/10 font-medium"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted"
                  )}
                >
                  <div className={cn(
                    "w-2 h-2 rounded-full",
                    isChildActive ? "bg-primary" : "bg-muted-foreground"
                  )} />
                  <span className="truncate">{child.title}</span>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  return (
    <Link
      to={path || "#"}
      onClick={onItemClick}
      className={cn(
        "flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-md transition-colors",
        computedIsActive
          ? "text-primary-foreground bg-primary"
          : "text-muted-foreground hover:text-foreground hover:bg-muted"
      )}
    >
      {icon}
      <span className="truncate">{title}</span>
    </Link>
  );
};

export default function Sidebar({ isOpen, closeSidebar }: SidebarProps) {
  return (
    <div className={cn(
      "fixed inset-y-0 left-0 z-50 transform bg-card border-r transition-transform duration-300 ease-in-out lg:relative lg:translate-x-0 w-[280px] max-w-[80vw]",
      isOpen ? "translate-x-0" : "-translate-x-full",
      "h-screen overflow-y-auto"
    )}>
      <div className="flex h-14 items-center justify-between border-b px-4">
        <Link to="/" className="flex items-center gap-2 font-semibold text-lg">
          <div className="h-6 w-6 rounded-md bg-primary flex items-center justify-center text-white">
            W
          </div>
          <span className="truncate">Wheelkart</span>
        </Link>

        {/* Close sidebar button - only visible on mobile */}
        <Button
          variant="ghost"
          size="icon"
          className="lg:hidden text-muted-foreground"
          onClick={closeSidebar}
          aria-label="Close sidebar"
        >
          <X className="h-5 w-5" />
        </Button>
      </div>
      <nav className="flex flex-col gap-1 p-4 overflow-y-auto max-h-[calc(100vh-3.5rem)]">
        <NavItem onItemClick={closeSidebar}
          icon={<LayoutDashboard className="h-5 w-5" />}
          title="Dashboard"
          path="/"
        />
        <NavItem onItemClick={closeSidebar}
          icon={<Users className="h-5 w-5" />}
          title="User Management"
          children={[
            { title: "Suppliers", path: "/suppliers" },
            { title: "Mobile Users", path: "/mobile-users" },
            { title: "Transporters", path: "/transporters" },
          ]}
        />
        <NavItem onItemClick={closeSidebar}
          icon={<Building className="h-5 w-5" />}
          title="Product Management"
          children={[
            { title: "Categories", path: "/categories" },
            { title: "Supplier Products", path: "/supplier-products" },
            { title: "Product Approvals", path: "/products/approvals" },
            { title: "Pricing", path: "/pricing" },
            { title: "Inventory", path: "/inventory" },
          ]}
        />
        <NavItem onItemClick={closeSidebar}
          icon={<ShoppingCart className="h-5 w-5" />}
          title="Order Management"
          children={[
            { title: "All Orders", path: "/orders" },
            { title: "Pending Orders", path: "/orders/pending" },
            { title: "Processing Orders", path: "/orders/processing" },
            { title: "Completed Orders", path: "/orders/completed" },
            { title: "Cancelled Orders", path: "/orders/cancelled" },
          ]}
        />
        <NavItem onItemClick={closeSidebar}
          icon={<DollarSign className="h-5 w-5" />}
          title="Earnings & Payments"
          children={[
            { title: "Payments Overview", path: "/earnings/overview" },
            { title: "Earnings Dashboard", path: "/earnings/dashboard" },
            { title: "Invoices", path: "/invoices" },
            { title: "Pending Payouts", path: "/earnings/pending-payouts" },
            { title: "Withdrawals", path: "/earnings/withdrawals" },
            { title: "Bank Verification", path: "/earnings/bank-verification" },
            { title: "Earnings History", path: "/earnings/history" },
            { title: "Batch Status", path: "/earnings/batch-status" },
            { title: "Statistics", path: "/earnings/statistics" },
          ]}
        />
        <NavItem onItemClick={closeSidebar}
          icon={<Megaphone className="h-5 w-5" />}
          title="Promotion Management"
          children={[
            { title: "Banners", path: "/marketing?tab=banners" },
            { title: "Offers", path: "/marketing?tab=offers" },
            { title: "Featured Deals", path: "/marketing?tab=deals" },
            { title: "Discount Codes", path: "/marketing?tab=discounts" },
            { title: "Notifications", path: "/marketing?tab=notifications" },
          ]}
        />
        <NavItem onItemClick={closeSidebar}
          icon={<FileText className="h-5 w-5" />}
          title="Content Management"
          children={[
            { title: "FAQ", path: "/content/faq" },
            { title: "Content & Legal", path: "/content" },
          ]}
        />
        <NavItem onItemClick={closeSidebar}
          icon={<Settings className="h-5 w-5" />}
          title="Settings"
          path="/settings"
        />
      </nav>
    </div>
  );
} 